import { TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import type { AdministratorRow } from "../../schema";
import type { AggregatorCreationRequestFieldErrors, FieldErrorValue } from "../../types";

interface FieldErrorsProps {
  fieldErrors?: AggregatorCreationRequestFieldErrors;
  administrators: AdministratorRow[];
}

const toMessage = (value: FieldErrorValue) => (Array.isArray(value) ? value.join(", ") : value);

const ErrorEntries = ({ errors }: { errors: Record<string, FieldErrorValue> }) => (
  <>
    {Object.entries(errors).map(([key, value]) => (
      <div key={key}>
        <span className="mr-2 ml-4 inline-block font-bold capitalize">
          {key.replace(/_/g, " ")}
        </span>
        {toMessage(value)}
      </div>
    ))}
  </>
);

export const FieldErrors = ({ fieldErrors, administrators }: FieldErrorsProps) => {
  const { t } = useTranslation();

  if (!fieldErrors) {
    return null;
  }

  const aggregatorErrors = fieldErrors.aggregator ?? {};
  const hasAggregatorErrors = Object.keys(aggregatorErrors).length > 0;
  const administratorErrors = (fieldErrors.administrators ?? []).filter(
    errors => errors && Object.keys(errors).length > 0
  );

  if (!hasAggregatorErrors && administratorErrors.length === 0) {
    return null;
  }

  return (
    <Alert variant="destructive" className="mb-6">
      <TriangleAlert />
      <AlertDescription className="block">
        {hasAggregatorErrors ? (
          <>
            <span className="my-2 block font-bold">
              {t(
                "representativeAggregatorCreationRequestDetails.form.aggregatorHasFollowingErrors"
              )}
            </span>
            <ErrorEntries errors={aggregatorErrors} />
          </>
        ) : null}
        {(fieldErrors.administrators ?? []).map((errors, index) =>
          errors && Object.keys(errors).length > 0 ? (
            <div key={administrators[index]?.id ?? index}>
              <span className="my-2 block font-bold">
                {t(
                  "representativeAggregatorCreationRequestDetails.form.administratorHasFollowingErrors",
                  { administrator: administrators[index]?.last_name }
                )}
              </span>
              <ErrorEntries errors={errors} />
            </div>
          ) : null
        )}
      </AlertDescription>
    </Alert>
  );
};
